import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import { supabase } from '../../utils/supabaseClient';

/**
 * Resolves an unmatched provider or service-type value by creating an alias
 * mapping. type is 'provider' | 'service'. onConfirm({ targetId }) is called;
 * the parent issues the create + recalc and closes on success.
 */
function MappingDialog({ open, type, uploadedName, onClose, onConfirm, submitting }) {
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const isProvider = type === 'provider';
  const label = isProvider ? 'Provider' : 'Service type';

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setLoadError('');
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const table = isProvider ? 'providers' : 'service_types';
        const { data, error } = await supabase.from(table).select('id, name').order('name');
        if (error) throw error;
        if (active) setOptions(data || []);
      } catch (e) {
        if (active) setLoadError(e.message || 'Failed to load options.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [open, isProvider]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Map {label.toLowerCase()}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Map the uploaded value <strong>“{uploadedName}”</strong> to an existing {label.toLowerCase()}.
          This creates a reusable alias and re-prices the batch.
        </DialogContentText>
        <Box sx={{ pt: 2 }}>
          {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
          <Autocomplete
            options={options}
            loading={loading}
            getOptionLabel={(o) => o.name || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={selected}
            onChange={(_, v) => setSelected(v)}
            renderInput={(params) => (
              <TextField {...params} label={`Select ${label.toLowerCase()}`} required />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!selected || submitting}
          onClick={() => onConfirm({ targetId: selected.id })}
        >
          {submitting ? 'Mapping…' : 'Map & recalculate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MappingDialog;
